import React, { useEffect } from 'react';
import { faChevronDown, faCog, faDownload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';

import GIButton from '@components/common/button/GIButton';
import { Popover, PopoverBody, PopoverTrigger } from '@components/common/popover/Popover';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import { BulkReassignTicketModal } from '../components/BulkReassignTicketModal';
import { CancelTicketModal } from '../components/CancelTicketModal';
import { CancelTicketReasonModal } from '../components/CancelTicketReasonModal';
import { CompleteTicketModal } from '../components/CompleteTicketModal';
import { ExportTicketsModal } from '../components/ExportTicketsModal';
import { MarkVerifyCompleteModal } from '../components/MarkVerifyCompleteModal';
import { ReassignTicketModal } from '../components/ReassignTicketModal';
import { ReopenTicketModal } from '../components/ReopenTicketModal';
import {
    StyledAction,
    StyledActionList,
    StyledDownloadButton,
    StyledHeaderButtonGroup,
    StyledPopoverli,
    StyledRecordsNotFound,
} from '../constants/ticketlist.styled';
import TicketTable from '../TicketTable/TicketTable';
import { useResultsSection } from './useResultsSection';
import { AddWatchersModal } from '../components/AddWatchersModal';
import { useModalDisclosures } from '../hooks/useModalDisclosures';

export const ResultsSection = (props: any) => {
    const {
        tickets,
        searchCriteria,
        ticketsCount,
        isAdvanceSearch,
        viewTicketPermission,
        resetTrigger,
        isWatchTicketAllowed,
        isManageWatcherAllowed,
    } = props;
    const intl = useIntl();

    const {
        isCancelTicketOpen,
        isCancelTicketReasonOpen,
        onCancelTicketClose,
        onCancelTicketReasonClose,
        isCompleteVerifyTicketOpen,
        onCompleteVerifyTicketClose,
        isCompleteTicketOpen,
        onCompleteTicketClose,
        onCancelTicketReasonOpen,
        isReassignTicketOpen,
        onReassignTicketOpen,
        onReassignTicketClose,
        isReopenTicketOpen,
        onReopenTicketClose,
        onCompleteTicketOpen,
        onCompleteVerifyTicketOpen,
        onCancelTicketOpen,
        onReopenTicketOpen,
        isBulkReassignTicketOpen,
        onBulkReassignTicketOpen,
        onBulkReassignTicketClose,
        isExportTicketsOpen,
        onExportTicketsOpen,
        onExportTicketsClose,
        isAddWatchersOpen,
        onAddWatchersOpen,
        onAddWatchersClose,
    } = useModalDisclosures();

    const {
        handleAddWatcherModal,
        selectedRows,
        handleBulkReassign,
        onRowSelect,
        handleBulkReassignTicketClose,
        tableRef,
        handleUpdateTicket,
        resetSelection,
    } = useResultsSection({
        onAddWatchersOpen,
        onBulkReassignTicketOpen,
        onBulkReassignTicketClose,
    });

    useEffect(() => {
        if (tableRef.current) {
            tableRef.current.resetSorting();
        }
    }, [resetTrigger]);

    return (
        <>
            <ConditionalRendering isTrue={!!tickets.length}>
                <ConditionalRendering isTrue={viewTicketPermission}>
                    <StyledHeaderButtonGroup>
                        <StyledDownloadButton>
                            <GIButton
                                id="downloadTicketsButton"
                                labelId=""
                                icon={<FontAwesomeIcon icon={faDownload} />}
                                isDisabled={false}
                                ariaLabel="Download Tickets"
                                variant="secondary"
                                onClickHandler={() => onExportTicketsOpen()}
                            />
                        </StyledDownloadButton>
                        <ConditionalRendering isTrue={!!selectedRows.length}>
                            <Popover>
                                <PopoverTrigger>
                                    <StyledAction data-testid="bulk-action-popover-trigger">
                                        <GIButton
                                            id="bulkActionButton"
                                            labelId="ticketList.btn.bulkAction"
                                            onClickHandler={() => {}}
                                            icon={<FontAwesomeIcon icon={faCog} />}
                                            iconPlacement="start"
                                            secondaryIcon={<FontAwesomeIcon icon={faChevronDown} />}
                                            secondaryIconPlacement="end"
                                        />
                                    </StyledAction>
                                </PopoverTrigger>
                                <PopoverBody top="-10%" left="100%">
                                    <StyledActionList>
                                        <StyledPopoverli
                                            data-testid="bulk-action-reassign"
                                            onClick={handleBulkReassign}
                                        >
                                            {intl.formatMessage({ id: 'ticketList.bulkActionPopover.reassign' })}
                                        </StyledPopoverli>
                                        <ConditionalRendering isTrue={isWatchTicketAllowed}>
                                            <StyledPopoverli
                                                data-testid="bulk-action-watch"
                                                onClick={() => handleUpdateTicket(true)}
                                            >
                                                {intl.formatMessage({ id: 'ticketList.bulkActionPopover.watch' })}
                                            </StyledPopoverli>
                                            <ConditionalRendering isTrue={isManageWatcherAllowed}>
                                                <StyledPopoverli
                                                    data-testid="bulk-action-add-watchers"
                                                    onClick={handleAddWatcherModal}
                                                >
                                                    {intl.formatMessage({
                                                        id: 'ticketList.bulkActionPopover.addWatchers',
                                                    })}
                                                </StyledPopoverli>
                                            </ConditionalRendering>
                                            <StyledPopoverli
                                                data-testid="bulk-action-unwatch"
                                                onClick={() => handleUpdateTicket(false)}
                                            >
                                                {intl.formatMessage({ id: 'ticketList.bulkActionPopover.unwatch' })}
                                            </StyledPopoverli>
                                        </ConditionalRendering>
                                    </StyledActionList>
                                </PopoverBody>
                            </Popover>
                        </ConditionalRendering>
                    </StyledHeaderButtonGroup>
                </ConditionalRendering>
                <TicketTable
                    ref={tableRef}
                    tickets={tickets}
                    searchCriteria={searchCriteria}
                    ticketsCount={ticketsCount}
                    isAdvanceSearch={isAdvanceSearch}
                    onRowSelect={onRowSelect}
                    onCompleteTicketOpen={onCompleteTicketOpen}
                    onCompleteVerifyTicketOpen={onCompleteVerifyTicketOpen}
                    onReassignTicketOpen={onReassignTicketOpen}
                    onCancelTicketOpen={onCancelTicketOpen}
                    onReopenTicketOpen={onReopenTicketOpen}
                    viewTicketPermission={viewTicketPermission}
                />
            </ConditionalRendering>
            <ConditionalRendering isTrue={!tickets.length}>
                <StyledRecordsNotFound data-testid="no-tickets-found">
                    {intl.formatMessage({ id: 'ticketList.noDataFound' })}
                </StyledRecordsNotFound>
            </ConditionalRendering>
            <ConditionalRendering isTrue={isCancelTicketOpen}>
                <CancelTicketModal onClose={onCancelTicketClose} onCancelTicketReasonOpen={onCancelTicketReasonOpen} />
            </ConditionalRendering>
            <ConditionalRendering isTrue={isCancelTicketReasonOpen}>
                <CancelTicketReasonModal onClose={onCancelTicketReasonClose} isAdvanceSearch={isAdvanceSearch} />
            </ConditionalRendering>
            <ConditionalRendering isTrue={isCompleteTicketOpen}>
                <CompleteTicketModal onClose={onCompleteTicketClose} isAdvanceSearch={isAdvanceSearch} />
            </ConditionalRendering>
            <ConditionalRendering isTrue={isCompleteVerifyTicketOpen}>
                <MarkVerifyCompleteModal onClose={onCompleteVerifyTicketClose} isAdvanceSearch={isAdvanceSearch} />
            </ConditionalRendering>
            <ConditionalRendering isTrue={isReassignTicketOpen}>
                <ReassignTicketModal onClose={onReassignTicketClose} />
            </ConditionalRendering>
            <ConditionalRendering isTrue={isReopenTicketOpen}>
                <ReopenTicketModal onClose={onReopenTicketClose} />
            </ConditionalRendering>
            <ConditionalRendering isTrue={isBulkReassignTicketOpen}>
                <BulkReassignTicketModal
                    onClose={handleBulkReassignTicketClose}
                    selectedRows={selectedRows}
                    isAdvanceSearch={isAdvanceSearch}
                />
            </ConditionalRendering>
            <ConditionalRendering isTrue={isExportTicketsOpen}>
                <ExportTicketsModal onClose={onExportTicketsClose} isAdvanceSearch={isAdvanceSearch} />
            </ConditionalRendering>
            <ConditionalRendering isTrue={isAddWatchersOpen}>
                <AddWatchersModal
                    onClose={onAddWatchersClose}
                    selectedRows={selectedRows}
                    resetSelection={resetSelection}
                />
            </ConditionalRendering>
        </>
    );
};
